/// <reference types="google.maps" />
import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { MapPin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import BookingDialog from '@/components/search/BookingDialog';
import { useBooking } from '@/hooks/useBooking';

interface ParkingSpot {
  id: string;
  owner_id: string;
  owner_name: string;
  address: string;
  vehicle_type: string;
  security_type: string;
  price_per_hour: number;
  parking_lot_count: number;
  images: string[];
  is_available: boolean;
  latitude?: number | null;
  longitude?: number | null;
  distance_km?: number;
}

const getStoredSpotPosition = (spot: ParkingSpot) => {
  const lat = Number(spot.latitude);
  const lng = Number(spot.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  // Reject (0,0) "null island" and other clearly-invalid placeholder coords
  if (Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001) return null;

  return { lat, lng };
};

interface MapViewProps {
  spots: ParkingSpot[];
  userLocation?: { lat: number; lng: number };
  onGetDirections: (address: string) => void;
}

const MapView = ({ spots, userLocation, onGetDirections }: MapViewProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const markers = useRef<google.maps.Marker[]>([]);
  const infoWindows = useRef<google.maps.InfoWindow[]>([]);
  const googleApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState<ParkingSpot | null>(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [geocodedPositions, setGeocodedPositions] = useState<Record<string, { lat: number; lng: number }>>({});
  const { toast } = useToast();
  const { handleBooking } = useBooking();

  useEffect(() => {
    if (!googleApiKey || !mapContainer.current) return;

    const loader = new Loader({
      apiKey: googleApiKey,
      version: 'weekly',
      libraries: ['places', 'geometry']
    });

    loader.load().then(() => {
      if (!mapContainer.current) return;

      try {
        map.current = new google.maps.Map(mapContainer.current, {
          center: userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : { lat: 28.6139, lng: 77.2090 },
          zoom: userLocation ? 13 : 10,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });

        if (userLocation) {
          new google.maps.Marker({
            position: { lat: userLocation.lat, lng: userLocation.lng },
            map: map.current,
            title: 'Your Location',
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#3b82f6',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          });
        }

        setMapLoaded(true);
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        toast({
          title: "Map Error",
          description: "Failed to initialize Google Maps. Please check your API key.",
          variant: "destructive",
        });
      }
    }).catch((error) => {
      console.error('Error loading Google Maps:', error);
      toast({
        title: "Map Error",
        description: "Failed to load Google Maps API.",
        variant: "destructive",
      });
    });

    return () => {
      markers.current.forEach(marker => marker.setMap(null));
      infoWindows.current.forEach(infoWindow => infoWindow.close());
      markers.current = [];
      infoWindows.current = [];
    };
  }, [googleApiKey, userLocation, toast]);

  useEffect(() => {
    if (!mapLoaded || !window.google?.maps?.Geocoder) return;

    const spotsNeedingCoordinates = spots.filter(
      spot => !getStoredSpotPosition(spot) && !geocodedPositions[spot.id] && spot.address?.trim()
    );

    if (spotsNeedingCoordinates.length === 0) return;

    const geocoder = new google.maps.Geocoder();
    let cancelled = false;
    const timers = spotsNeedingCoordinates.map((spot, index) => window.setTimeout(() => {
      geocoder.geocode(
        {
          address: spot.address,
          componentRestrictions: { country: 'IN' },
        },
        (results, status) => {
          if (cancelled) return;

          if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
            const result = results[0];
            const locationType = result.geometry.location_type;
            // Reject low-quality matches that often resolve to random points (e.g. ocean)
            if (
              result.partial_match ||
              locationType === google.maps.GeocoderLocationType.APPROXIMATE
            ) {
              console.warn('Skipping low-confidence geocode for:', spot.address, locationType, 'partial:', result.partial_match);
              return;
            }
            const location = result.geometry.location;
            setGeocodedPositions(prev => ({
              ...prev,
              [spot.id]: { lat: location.lat(), lng: location.lng() },
            }));
          } else {
            console.warn('Could not map parking spot address:', spot.address, status);
          }
        }
      );
    }, index * 150));

    return () => {
      cancelled = true;
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, [spots, mapLoaded, geocodedPositions]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    markers.current.forEach(marker => marker.setMap(null));
    infoWindows.current.forEach(infoWindow => infoWindow.close());
    markers.current = [];
    infoWindows.current = [];

    const bounds = new google.maps.LatLngBounds();
    let hasMarkers = false;

    if (userLocation) {
      bounds.extend({ lat: userLocation.lat, lng: userLocation.lng });
      hasMarkers = true;
    }

    spots.forEach(spot => {
      const pos = getStoredSpotPosition(spot) ?? geocodedPositions[spot.id];

      if (pos && map.current) {
        bounds.extend(pos);
        hasMarkers = true;

        const markerColor = spot.is_available ? '#22c55e' : '#ef4444';

        const marker = new google.maps.Marker({
          position: pos,
          map: map.current,
          title: spot.address,
          label: {
            text: `P${spot.parking_lot_count}`,
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 'bold',
          },
          icon: {
            path: 'M-12,-24 C-12,-24 -12,0 0,0 C12,0 12,-24 12,-24 C12,-36 -12,-36 -12,-24 Z',
            fillColor: markerColor,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 1,
            labelOrigin: new google.maps.Point(0, -20),
          },
        });

        const imageHtml = spot.images && spot.images.length > 0
          ? `<img src="${spot.images[0]}" style="width:100%;height:80px;object-fit:cover;border-radius:6px;margin-bottom:6px;" />`
          : '';

        const distanceHtml = spot.distance_km
          ? `<div style="color:#3b82f6;font-size:12px;margin-top:4px;">📍 ${spot.distance_km} km away</div>`
          : '';

        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding:8px;min-width:220px;max-width:280px;font-family:system-ui;">
              ${imageHtml}
              <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${spot.address}</div>
              <div style="display:flex;gap:8px;align-items:center;margin-bottom:4px;">
                <span style="background:#22c55e;color:white;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;">₹${spot.price_per_hour}/hr</span>
                <span style="background:#f1f5f9;padding:2px 8px;border-radius:12px;font-size:11px;">${spot.parking_lot_count} lots</span>
              </div>
              <div style="color:#666;font-size:12px;">${spot.vehicle_type} • ${spot.security_type}</div>
              ${distanceHtml}
              <div style="display:flex;gap:6px;margin-top:8px;">
                <button onclick="window.bookParkingSpot('${spot.id}')" style="flex:1;background:#3b82f6;color:white;border:none;padding:6px 10px;border-radius:6px;font-size:12px;cursor:pointer;font-weight:500;">Book Now</button>
                <button onclick="window.getSpotDirections('${spot.address.replace(/'/g, "\\'")}')" style="flex:1;background:#f1f5f9;color:#333;border:none;padding:6px 10px;border-radius:6px;font-size:12px;cursor:pointer;font-weight:500;">Directions</button>
              </div>
            </div>
          `,
        });

        marker.addListener('click', () => {
          infoWindows.current.forEach(iw => iw.close());
          infoWindow.open(map.current, marker);
        });

        markers.current.push(marker);
        infoWindows.current.push(infoWindow);
      }
    });

    // Auto-fit bounds
    if (hasMarkers && markers.current.length > 0) {
      map.current.fitBounds(bounds);
      const listener = google.maps.event.addListener(map.current, 'idle', () => {
        const currentZoom = map.current?.getZoom();
        if (currentZoom && currentZoom > 16) {
          map.current?.setZoom(16);
        }
        google.maps.event.removeListener(listener);
      });
    }

    (window as any).bookParkingSpot = (spotId: string) => {
      const spot = spots.find(s => s.id === spotId);
      if (spot) {
        setSelectedSpot(spot);
        setBookingDialogOpen(true);
      }
    };

    (window as any).getSpotDirections = (address: string) => {
      onGetDirections(address);
    };

  }, [spots, mapLoaded, onGetDirections, userLocation, geocodedPositions]);

  if (!googleApiKey) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-muted rounded-lg p-8">
        <MapPin className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">Map View Not Available</h3>
        <p className="text-muted-foreground text-center">
          Google Maps is not configured. Please contact the administrator.
        </p>
      </div>
    );
  }

  const spotsWithCoords = spots.filter(s => getStoredSpotPosition(s) || geocodedPositions[s.id]);

  return (
    <div className="relative w-full h-[600px] rounded-lg overflow-hidden shadow-lg">
      <div ref={mapContainer} className="absolute inset-0" />
      
      {/* Spot count badge */}
      {mapLoaded && (
        <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm text-foreground px-3 py-1.5 rounded-full text-sm font-medium shadow-md border border-border z-10">
          📍 Showing {spotsWithCoords.length} spot{spotsWithCoords.length !== 1 ? 's' : ''}
        </div>
      )}

      {selectedSpot && (
        <BookingDialog
          open={bookingDialogOpen}
          onOpenChange={setBookingDialogOpen}
          onBook={(date, time, hours, vehicleNumber, lotCount) =>
            handleBooking(selectedSpot.id, selectedSpot.price_per_hour, date, time, hours, vehicleNumber, lotCount)
          }
          parkingSpotId={selectedSpot.id}
          maxLots={selectedSpot.parking_lot_count}
        >
          <div></div>
        </BookingDialog>
      )}
    </div>
  );
};

export default MapView;
